import { useState } from 'react';
import { IconClose } from 'shared/assets/icons';
import { FieldError } from 'shared/components/Input';
import { addSkillValue, removeSkillValue } from 'auth/profileForm';

export function SkillsInput({
  id,
  label,
  hint,
  error,
  required = false,
  value,
  onChange,
  maxItems = 20,
  maxLength = 80,
  disabled = false,
  addLabel,
  removeLabel,
  addedAnnouncement,
  removedAnnouncement,
  placeholder,
}) {
  const [draft, setDraft] = useState('');
  const [liveMessage, setLiveMessage] = useState('');
  const skills = Array.isArray(value) ? value : [];
  const hintId = hint ? `${id}-hint` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  const described = [hintId, errorId].filter(Boolean).join(' ') || undefined;
  const limits = { max_items: maxItems, max_length: maxLength };

  function announce(message) {
    setLiveMessage(message);
  }

  function commit(raw) {
    const next = addSkillValue(skills, raw, limits);
    if (next.length !== skills.length) {
      onChange(next);
      const added = next[next.length - 1];
      if (addedAnnouncement) {
        announce(addedAnnouncement.replace('{skill}', added));
      }
    }
    setDraft('');
  }

  function removeAt(index) {
    const removed = skills[index];
    const next = removeSkillValue(skills, index);
    onChange(next);
    if (removed && removedAnnouncement) {
      announce(removedAnnouncement.replace('{skill}', removed));
    }
  }

  return (
    <div className="field skills-field">
      <div className="field-label-row">
        <label className="field-label" htmlFor={id}>{label}</label>
        {required ? <span className="required-mark" aria-hidden="true">*</span> : null}
      </div>
      <div className={['skills-box', error ? 'input-error' : ''].filter(Boolean).join(' ')}>
        {skills.map((skill, index) => (
          <span key={`${skill}-${index}`} className="skill-chip">
            <span>{skill}</span>
            <button
              type="button"
              className="skill-chip-remove"
              onClick={() => removeAt(index)}
              disabled={disabled}
              aria-label={(removeLabel || 'Remove').replace('{skill}', skill)}
            >
              <IconClose />
            </button>
          </span>
        ))}
        <input
          id={id}
          className="skills-draft"
          value={draft}
          disabled={disabled || skills.length >= maxItems}
          placeholder={placeholder}
          autoComplete="off"
          aria-invalid={error ? true : undefined}
          aria-required={required || undefined}
          aria-describedby={described}
          onChange={(event) => {
            const next = event.target.value;
            if (/[,،]/.test(next)) {
              commit(next);
              return;
            }
            setDraft(next);
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              commit(draft);
            }
          }}
        />
        <button
          type="button"
          className="btn btn-ghost skills-add"
          onClick={() => commit(draft)}
          disabled={disabled || !draft.trim() || skills.length >= maxItems}
        >
          {addLabel}
        </button>
      </div>
      {hint ? <p id={hintId} className="field-hint">{hint}</p> : null}
      <p className="sr-only" aria-live="polite">{liveMessage}</p>
      <FieldError id={errorId}>{error}</FieldError>
    </div>
  );
}
